/**
 * Correcting an attendance record's time logs — the mobile half of ERPat's web
 * "Edit Time Logs" form, over `PATCH /v1/api/attendance/{id}`.
 *
 * The usual reason is a `clockout` record: the employee never clocked out, so
 * ERPat's auto clock-out job closed the shift at its own clock-in (out = in,
 * note "System Clockout"). Its figures measure nothing, and it cannot be
 * approved until someone enters the real times. The server says so with the
 * row's `needs_correction` flag and the detail route's `edit` block.
 *
 * Everything here is pure (no I/O) so it is unit-tested directly:
 *  - reading the server's `edit` verdict — which FAILS CLOSED, unlike the
 *    approval gate: an older server has no edit endpoint, so no block means no
 *    edit button;
 *  - the stored breaks as positional pairs;
 *  - the "what happens next" copy for someone who cannot correct it;
 *  - the edit form's validation and the request body it produces.
 *
 * TIMES are the company's LOCAL wall-clock strings end to end ("YYYY-MM-DD HH:MM").
 * Nothing here builds a `Date`: the device's time zone can differ from the
 * company's, and the server converts to UTC itself.
 */
import { asRecord, pick, type Json } from '@/services/api/http';
import { OPEN_ATTENDANCE_LOCK, attendanceLockLead, type AttendanceLockVM } from '@/services/api/attendance-lock';

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const v = pick<unknown>(rec, keys, fallback);
  return v == null ? fallback : String(v);
};

const bool = (rec: Json, keys: string[], fallback = false): boolean => {
  const v = pick<unknown>(rec, keys, fallback);
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['1', 'true', 'yes', 'on'].includes(v.toLowerCase());
  return fallback;
};

// ── The server's edit verdict ────────────────────────────────────────────────

/** Why the server would refuse this caller an edit ('' when it did not say). */
export type AttendanceEditBlockReason =
  | 'no_permission'
  | 'self_not_allowed'
  | 'not_editable'
  | 'attendance_locked'
  | '';

export interface AttendanceEditVM {
  /** This caller may correct this record's time logs. */
  allowed: boolean;
  reason: AttendanceEditBlockReason;
  /** The record belongs to the caller. */
  selfRecord: boolean;
  /** A `schedule` log: the correction must name a schedule template. */
  requiresSchedule: boolean;
  /** The most break pairs a correction may carry. */
  maxBreakPairs: number;
  /** The server sent an `edit` block at all (detail route, new enough backend). */
  known: boolean;
}

/** No block → no edit. Four pairs is the floor every tenant allows. */
export const CLOSED_ATTENDANCE_EDIT: AttendanceEditVM = {
  allowed: false,
  reason: '',
  selfRecord: false,
  requiresSchedule: false,
  maxBreakPairs: 4,
  known: false,
};

function readEditReason(value: unknown): AttendanceEditBlockReason {
  const s = String(value ?? '').toLowerCase().trim();
  return s === 'no_permission' || s === 'self_not_allowed' || s === 'not_editable' || s === 'attendance_locked'
    ? s
    : '';
}

/** Read a row's `edit` block, failing CLOSED when it is absent or malformed. */
export function readAttendanceEdit(r: Json): AttendanceEditVM {
  const raw = pick<unknown>(r, ['edit'], null);
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return { ...CLOSED_ATTENDANCE_EDIT };

  const e = asRecord(raw);
  const maxPairs = Number(pick<unknown>(e, ['max_break_pairs'], 4));
  return {
    allowed: bool(e, ['allowed']),
    reason: readEditReason(pick(e, ['reason'], '')),
    selfRecord: bool(e, ['self_record']),
    requiresSchedule: bool(e, ['requires_schedule'], str(e, ['log_type'], 'schedule') === 'schedule'),
    maxBreakPairs: Number.isFinite(maxPairs) && maxPairs >= 1 ? Math.floor(maxPairs) : 4,
    known: true,
  };
}

// ── Breaks ───────────────────────────────────────────────────────────────────

/** One stored break: a start tap and its end tap, company-local ('' = absent). */
export interface AttendanceBreakPairVM {
  startLocal: string;
  endLocal: string;
}

/** The detail route's positional break pairs. Order is meaning — never filtered. */
export function readAttendanceBreaks(r: Json): AttendanceBreakPairVM[] {
  const raw = pick<unknown>(r, ['breaks'], null);
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const p = asRecord(item);
    return {
      startLocal: str(p, ['start_local'], ''),
      endLocal: str(p, ['end_local'], ''),
    };
  });
}

// ── Schedule templates ───────────────────────────────────────────────────────

export interface AttendanceScheduleOptionVM {
  id: string;
  title: string;
  /** A short second line, e.g. "5 days · 8:00 PM – 5:00 AM · 8h/day" ('' when unknown). */
  summary: string;
}

function hoursText(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '';
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}h/day`;
}

/** Map `GET /v1/api/attendance/schedules` → picker options. */
export function mapAttendanceScheduleOptions(data: unknown): AttendanceScheduleOptionVM[] {
  const body = asRecord(data);
  const list = pick<unknown>(body, ['schedules'], Array.isArray(data) ? data : []);
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      const s = asRecord(item);
      const days = pick<unknown>(s, ['days'], []);
      const firstWorking = Array.isArray(days)
        ? days.map(asRecord).find((d) => bool(d, ['enabled']) && str(d, ['in'], '') !== '')
        : undefined;
      const workingDays = Number(pick<unknown>(s, ['working_days'], 0));
      const parts = [
        Number.isFinite(workingDays) && workingDays > 0 ? `${workingDays} day${workingDays === 1 ? '' : 's'}` : '',
        firstWorking ? `${str(firstWorking, ['in'], '')} – ${str(firstWorking, ['out'], '')}` : '',
        hoursText(Number(pick<unknown>(s, ['avg_hours'], 0))),
      ].filter(Boolean);
      return {
        id: str(s, ['id'], ''),
        title: str(s, ['title', 'name'], ''),
        summary: parts.join(' · '),
      };
    })
    .filter((o) => o.id !== '' && o.title !== '');
}

// ── What happens next (for someone who cannot correct it) ────────────────────

/**
 * The next step for a viewer who can SEE a record that needs correcting but
 * cannot correct it themselves — who to ask, and what happens after. '' when the
 * record does not need correcting or the viewer can correct it.
 */
export function attendanceNextStep(input: {
  needsCorrection: boolean;
  edit: AttendanceEditVM;
  /** Fallback owner flag when the server sent no edit block. */
  selfRecord?: boolean;
  /**
   * Which lock refused the edit (`approval.lock`) — the payroll period, or the
   * company's "Days to Lock Attendance" window. Absent reads as the payroll period.
   */
  lock?: AttendanceLockVM;
}): string {
  if (!input.needsCorrection || input.edit.allowed) return '';

  const self = input.edit.known ? input.edit.selfRecord : !!input.selfRecord;

  if (input.edit.reason === 'attendance_locked') {
    const lead = attendanceLockLead(input.lock ?? OPEN_ATTENDANCE_LOCK);
    return self
      ? `${lead}, so your time logs can’t be corrected here. Ask HR or payroll to adjust them.`
      : `${lead}, so these time logs can’t be corrected here. Ask HR or payroll to adjust them.`;
  }

  if (self) {
    return 'You didn’t clock out, so the system closed this shift at your clock-in time. Ask your supervisor or HR to enter your actual clock-out — once corrected, it goes back to Pending for approval.';
  }

  return 'No clock-out was recorded, so the system closed this shift at its clock-in time. Ask HR or someone who can update attendance to correct the time logs — once corrected, it goes back to Pending for approval.';
}

// ── The edit form ────────────────────────────────────────────────────────────

export interface AttendanceEditBreakRow {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

/** Native date + time input values: 'YYYY-MM-DD' and 'HH:MM' (24h). */
export interface AttendanceEditForm {
  inDate: string;
  inTime: string;
  outDate: string;
  outTime: string;
  breaks: AttendanceEditBreakRow[];
  schedId: string;
  note: string;
}

/** What the form needs to know about the record it edits. */
export interface AttendanceEditSource {
  inTimeLocal: string;
  outTimeLocal: string;
  needsCorrection: boolean;
  breaks: AttendanceBreakPairVM[];
  schedId: string;
  /** The note as plain text (rich web notes already flattened for display). */
  noteText: string;
}

const LOCAL_RE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})/;

/** Split a local "YYYY-MM-DD HH:MM[:SS]" into the two input values. */
export function splitLocal(value: string): { date: string; time: string } {
  const m = String(value ?? '').match(LOCAL_RE);
  return m ? { date: m[1] ?? '', time: `${m[2]}:${m[3]}` } : { date: '', time: '' };
}

/** Join the two input values back into "YYYY-MM-DD HH:MM" ('' when either is empty). */
export function joinLocal(date: string, time: string): string {
  return date && time ? `${date} ${time}` : '';
}

/** Minute-precision key of a local value, for comparing without its seconds. */
function minuteKey(value: string): string {
  const { date, time } = splitLocal(value);
  return joinLocal(date, time);
}

/** Prefill the form from the record. */
export function attendanceEditFormFrom(src: AttendanceEditSource): AttendanceEditForm {
  const inParts = splitLocal(src.inTimeLocal);
  // A system clock-out stored its clock-in as the clock-out — that is the value
  // being corrected, so it must not be offered back as an answer.
  const outParts = src.needsCorrection
    ? { date: inParts.date, time: '' }
    : splitLocal(src.outTimeLocal);

  return {
    inDate: inParts.date,
    inTime: inParts.time,
    outDate: outParts.date,
    outTime: outParts.time,
    breaks: src.breaks.map((b) => {
      const start = splitLocal(b.startLocal);
      const end = splitLocal(b.endLocal);
      return { startDate: start.date, startTime: start.time, endDate: end.date, endTime: end.time };
    }),
    schedId: src.schedId,
    note: src.noteText,
  };
}

export interface AttendanceEditErrors {
  inTime?: string;
  outTime?: string;
  schedule?: string;
  /** Per break row, by index. */
  breaks: Record<number, string>;
  /** A problem with the break list as a whole. */
  breakList?: string;
}

export interface AttendanceEditValidation {
  valid: boolean;
  errors: AttendanceEditErrors;
}

function isBlankRow(row: AttendanceEditBreakRow): boolean {
  return !row.startDate && !row.startTime && !row.endDate && !row.endTime;
}

/**
 * Validate the form. Stricter than the server on breaks on purpose: a
 * correction closes a shift, so an open break (a start with no end) or a break
 * outside the shift is a mistake worth catching here. Wholly empty break rows
 * are ignored — they are dropped from the request.
 */
export function validateAttendanceEdit(
  form: AttendanceEditForm,
  opts: { requiresSchedule: boolean; maxBreakPairs: number },
): AttendanceEditValidation {
  const errors: AttendanceEditErrors = { breaks: {} };

  const inAt = joinLocal(form.inDate, form.inTime);
  const outAt = joinLocal(form.outDate, form.outTime);

  if (!inAt) errors.inTime = 'Enter the clock-in date and time.';
  if (!outAt) errors.outTime = 'Enter the clock-out date and time.';
  else if (inAt && outAt <= inAt) errors.outTime = 'Clock-out must be after clock-in.';

  const filled = form.breaks.filter((row) => !isBlankRow(row));
  if (filled.length > opts.maxBreakPairs) {
    errors.breakList = `Your company allows up to ${opts.maxBreakPairs} breaks.`;
  }

  form.breaks.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const start = joinLocal(row.startDate, row.startTime);
    const end = joinLocal(row.endDate, row.endTime);
    if ((row.startDate && !row.startTime) || (!row.startDate && row.startTime)
      || (row.endDate && !row.endTime) || (!row.endDate && row.endTime)) {
      errors.breaks[index] = 'Enter both a date and a time.';
    } else if (!start || !end) {
      errors.breaks[index] = 'Enter when the break started and ended.';
    } else if (end < start) {
      errors.breaks[index] = 'The break can’t end before it starts.';
    } else if (inAt && outAt && (start < inAt || end > outAt)) {
      errors.breaks[index] = 'Breaks must fall within the shift.';
    }
  });

  if (opts.requiresSchedule && !form.schedId) errors.schedule = 'Choose the schedule for this shift.';

  const valid = !errors.inTime && !errors.outTime && !errors.schedule && !errors.breakList
    && Object.keys(errors.breaks).length === 0;
  return { valid, errors };
}

/** The `PATCH /v1/api/attendance/{id}` body, in the server's own field names. */
export interface AttendanceEditBody {
  in_time: string;
  out_time: string;
  breaks: Array<{ start: string; end: string }>;
  sched_id?: number;
  note?: string;
}

/**
 * Build the request body. An UNTOUCHED time is sent back as the stored value,
 * seconds included, so a correction to the clock-out does not quietly shave
 * seconds off the clock-in (the form has no seconds field). The note travels
 * only when it was actually changed, so a rich-text note written on the web is
 * never flattened by a correction that did not touch it.
 */
export function buildAttendanceEditBody(
  form: AttendanceEditForm,
  src: AttendanceEditSource,
  opts: { requiresSchedule: boolean },
): AttendanceEditBody {
  const keepSeconds = (typed: string, stored: string): string =>
    stored && minuteKey(stored) === typed ? stored.replace('T', ' ') : typed;

  const inAt = joinLocal(form.inDate, form.inTime);
  const outAt = joinLocal(form.outDate, form.outTime);

  // Stored break taps by their minute, so an untouched tap keeps its seconds
  // wherever its row now sits (removing a row shifts every index after it).
  const storedTaps = new Map<string, string>();
  for (const pair of src.breaks) {
    for (const tap of [pair.startLocal, pair.endLocal]) {
      const key = minuteKey(tap);
      if (key && !storedTaps.has(key)) storedTaps.set(key, tap.replace('T', ' '));
    }
  }
  const keepTapSeconds = (typed: string): string => (typed && storedTaps.get(typed)) || typed;

  const body: AttendanceEditBody = {
    in_time: keepSeconds(inAt, src.inTimeLocal),
    out_time: src.needsCorrection ? outAt : keepSeconds(outAt, src.outTimeLocal),
    breaks: form.breaks
      .filter((row) => !isBlankRow(row))
      .map((row) => ({
        start: keepTapSeconds(joinLocal(row.startDate, row.startTime)),
        end: keepTapSeconds(joinLocal(row.endDate, row.endTime)),
      })),
  };

  if (opts.requiresSchedule && form.schedId) {
    const id = Number(form.schedId);
    if (Number.isInteger(id) && id > 0) body.sched_id = id;
  }

  if (form.note.trim() !== src.noteText.trim()) body.note = form.note.trim();

  return body;
}
